import { flow, getEnv, getSnapshot, Instance, types } from "mobx-state-tree";
import { computedFn } from "mobx-utils";
import { JsonPatch } from "json8-patch";

import { ApplicationError } from "../error-handler/ErrorHandlerService";
import { LoggerModel } from "../loggers/LoggerModel";
import { globalModule } from "../service-provider/GlobalModule";

export const StorageModel = types
  .model("StorageModel", {
    autoLoad: true,
    data: types.frozen(),
    error: types.maybe(types.frozen<ApplicationError>()),
    cacheKey: types.maybe(types.string),
    logger: types.optional(LoggerModel, {
      enabled: false,
    }),
    performDownload: false,
    /**
     * Whether load() has run to completion at least once — INCLUDING the paths that fetch
     * nothing: `canLoadItems === false`, and a 200 whose body is null/empty.
     *
     * Without this, "never loaded" and "loaded, and there was nothing" are indistinguishable,
     * so `isLoading` reported the second as still-loading FOREVER. That is what wedged the
     * App.tsx boot gate on a permanent "loading..." splash whenever a signed-in user had no
     * organization: the org -> subscription -> permissions chain stalls, no error is ever set,
     * and the only escape was clearing the MSAL cache out of localStorage by hand.
     */
    loadAttempted: false,
  })
  .views((self) => ({
    get canLoadItems() {
      return true;
    },
    getDataQuery() {
      return "";
    },
    get endpoint() {
      return {};
    },
    get requestBody() {
      return null;
    },
    mapResponse(response: any) {
      self.data = response;
    },
    get isLoading() {
      // Having finished trying is NOT the same as still loading. An absent response and
      // "this store cannot load at all right now" are both legitimate terminal states.
      if (self.error || self.loadAttempted) return false;
      return !self.data;
    },
    resetData() {
      self.data = undefined;
    },
  }))
  // Its own action so load() can flip the flag from a `finally` block. A plain async action
  // loses MST's action context across `await`, so assigning to `self` there would throw
  // "cannot modify outside of actions"; calling an action is always allowed.
  .actions((self) => ({
    setLoadAttempted(attempted: boolean) {
      self.loadAttempted = attempted;
    },
  }))
  .actions((self) => {
    const getDataFromCache = () => {
      return self.cacheKey
        ? getEnv(self).appSessionStorage.getStorageItem(self.cacheKey)
        : undefined;
    };
    const loadDataFromAPI = flow(function* () {
      try {
        self.logger.debug("calling api for", self.endpoint);
        self.logger.debug("api:", getEnv(self).api);
        let response = undefined;

        if (self.performDownload) response = yield getEnv(self).api.download(self.endpoint);
        else if (self.requestBody === null) response = yield getEnv(self).api.get(self.endpoint);
        else response = (yield getEnv(self).api.post(self.endpoint, self.requestBody)).data;

        self.logger.debug("data from api", response);
        saveDataInCache(response);
        self.mapResponse(response);
      } catch (error) {
        self.logger.debug("storageModel error", error);
        self.error = error as ApplicationError;
        globalModule.errorHandler?.handleErrorInSnackbar(self.error);
      }
    });
    const saveDataInCache = (data: any) => {
      if (self.cacheKey) {
        getEnv(self).appSessionStorage.setStorageItem(self.cacheKey, data);
      }
    };

    return {
      async load() {
        self.logger.debug("canLoadItems:", self.canLoadItems);
        self.resetData();
        // Back to "loading" for the duration, so a genuine reload still shows a spinner.
        self.setLoadAttempted(false);
        try {
          if (!self.canLoadItems) return;

          let cachedData = getDataFromCache();
          self.logger.debug("cached data:", cachedData);
          if (cachedData) {
            self.mapResponse(cachedData);
          } else {
            await loadDataFromAPI();
          }
        } finally {
          // Every exit path, including the canLoadItems early return above and a thrown
          // request — otherwise that path is exactly the one that hangs the boot gate.
          self.setLoadAttempted(true);
        }
      },
      async patchDetails(data: JsonPatch) {
        if (!data.length) return;

        return await getEnv(self).api.patch(self.endpoint, data);
      },
    };
  })
  .actions((self) => ({
    async afterCreate() {
      self.logger.debug("create({", getSnapshot(self), "}");
      if (self.autoLoad) {
        await self.load();
      }
    },
    setData(data: any) {
      self.data = data;
    },
  }));

export type Storage = Instance<typeof StorageModel>;

export const areLoading = computedFn((stores: Array<Storage>) =>
  stores.some((store) => store.isLoading)
);
